// Pure helpers to turn a filter "input" object (the same shape FilterChips
// consumes) into display-ready { key, label, value } entries. Shared between
// the FilterChips UI and the read-only "detail boxes" UI so both stay in sync.

// Resolve a value to date parts, or null when it isn't a date. Strings are calendar
// dates and are read in UTC, so a "...T00:00:00Z" bound shows the same day the Bond
// Details panel shows whatever the viewer's timezone; Date objects already carry local
// parts, so those stay local.
const getDateParts = (val) => {
  if (val instanceof Date && !isNaN(val)) {
    return { y: val.getFullYear(), m: val.getMonth() + 1, d: val.getDate() };
  }
  if (typeof val !== "string") return null;
  const match = val.match(/^\s*(\d{4})[-\s\/](\d{1,2})[-\s\/](\d{1,2})(?:[T\s].*)?\s*$/);
  const parsed = match
    ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
    : new Date(val);
  if (isNaN(parsed)) return null;
  return { y: parsed.getUTCFullYear(), m: parsed.getUTCMonth() + 1, d: parsed.getUTCDate() };
};

const pad2 = (n) => String(n).padStart(2, "0");

// Detect common date patterns and Date objects
const isDateValue = (v) => {
  if (v instanceof Date && !isNaN(v)) return true;
  if (typeof v !== "string") return false;
  // Trailing time part is optional: without it a timestamped bound skips the date
  // branch below and gets rendered as a raw ISO string
  return /^\d{4}[-\s\/]\d{1,2}[-\s\/]\d{1,2}([T\s].*)?$/.test(v);
};

// Format date-like values as "12Nov25"
const formatDateShort = (val) => {
  if (val == null || val === "") return "";
  const parts = getDateParts(val);
  if (!parts) return val;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${pad2(parts.d)}${months[parts.m - 1] || ""}${String(parts.y).slice(-2)}`;
};

// Format to MM/DD/YYYY for maturity display
const formatDateToMMDDYYYY = (val) => {
  if (val == null || val === "") return "";
  const parts = getDateParts(val);
  if (!parts) return val;
  return `${pad2(parts.m)}/${pad2(parts.d)}/${parts.y}`;
};

const hasRangeValue = (value) =>
  value !== undefined && value !== null && value !== "";

// Limit numeric values to a maximum of 5 decimal places (no forced trailing zeros).
const formatDecimal = (value) => {
  if (value === null || value === undefined || value === "" || typeof value === "boolean") {
    return value;
  }
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return Number(num.toFixed(5)).toString();
};

const shouldShowZeroLowerBound = (key, max) => {
  if (!hasRangeValue(max)) return false;
  const normalizedKey = `${key || ""}`.toLowerCase();
  const nonNegativeRangeKeys = ["coupon", "maturity", "price", "duration"];
  return nonNegativeRangeKeys.some((rangeKey) => normalizedKey.includes(rangeKey));
};

export const formatRange = (min, max, key) => {
  const isMaturityKey = typeof key === "string" && key.toLowerCase().includes("maturity");
  const hasMin = hasRangeValue(min);
  const hasMax = hasRangeValue(max);

  if (isDateValue(min) || isDateValue(max)) {
    const fmt = isMaturityKey ? formatDateToMMDDYYYY : formatDateShort;
    const a = hasMin ? fmt(min) : "";
    const b = hasMax ? fmt(max) : "";
    if (a && b) return `${a} to ${b}`;
    if (a) return `${a}+`;
    if (b) return `Up to ${b}`;
    return "";
  }

  const minFmt = formatDecimal(min);
  const maxFmt = formatDecimal(max);
  if (hasMin && hasMax) return `${minFmt} to ${maxFmt}`;
  if (hasMin) return `${minFmt}+`;
  if (hasMax && shouldShowZeroLowerBound(key, max)) return `0 to ${maxFmt}`;
  if (hasMax) return `Up to ${maxFmt}`;
  return "";
};

export const formatBooleanValue = (key, value) => {
  const normalizedKey = typeof key === "string" ? key.split("__")[0] : key;
  const booleanFields = [
    "zeroCoupon",
    "insured",
    "altMinTax",
    "taxable",
    "callable",
    "call",
    "perpetual",
  ];
  if (booleanFields.includes(normalizedKey)) {
    const normalizedValue = `${value}`.trim().toLowerCase();
    return normalizedValue === "1" || normalizedValue === "y" || normalizedValue === "yes"
      ? "Yes"
      : "No";
  }
  if (normalizedKey === "minMaturity") {
    return formatDateToMMDDYYYY(value);
  }
  if (typeof normalizedKey === "string" && normalizedKey.toLowerCase().includes("maturity")) {
    return formatDateToMMDDYYYY(value);
  }
  if (
    (typeof normalizedKey === "string" && normalizedKey.toLowerCase().includes("date")) ||
    isDateValue(value)
  ) {
    return formatDateShort(value);
  }
  if (normalizedKey === "securityCharge" && Array.isArray(value)) {
    return value.join(", ");
  }
  return formatDecimal(value);
};

export const formatFieldLabel = (key) => {
  const baseKey = typeof key === "string" ? key.split("__")[0] : key;
  const labelMap = {
    minimumRating: "Min S&P Rating",
    minimumRatingMoody: "Min Moody's Rating",
    underlyingRating: "Underlying Rating",
    paymentFrequency: "Payment Freq",
    incomeSource: "Income Source",
    securityCharge: "Factored Securities",
    altMinTax: "Alt Min Tax",
    zeroCoupon: "Zero Coupon",
    callable: "Callable",
    call: "Callable",
    insured: "Insured",
    perpetual: "Perpetual",
    taxable: "Taxable",
    callStartDate: "Call Start Date",
    nextCallDate: "Next Call Date",
    issuer: "Issuer",
    description: "Desc",
    textIncludes: "Includes",
    textExcludes: "Excludes",
  };

  if (!baseKey || typeof baseKey !== "string") return baseKey;
  if (labelMap[baseKey]) return labelMap[baseKey];

  const spaced = baseKey
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_\-\.\:]+/g, " ")
    .trim();

  const words = spaced.split(/\s+/).map((w) => {
    if (w.toUpperCase() === w) return w;
    return w.charAt(0).toUpperCase() + w.slice(1);
  });

  return words.join(" ");
};

/**
 * Convert a filter input object into display-ready entries.
 * Mirrors the chip-building logic in FilterChips so the detail-box UI shows
 * the same labels/values. Returns an array of { key, label, value }.
 */
export const getFilterDisplayEntries = (input) => {
  if (!input) return [];

  const entries = Object.entries(input)
    .filter(([key]) => key !== "source")
    .filter(([, value]) => value !== undefined && value !== "");

  const processedRangeKeys = new Set();
  const result = [];

  entries.forEach(([key, value]) => {
    // Fields that look like ranges but are not (rating fields)
    if (key === "minimumRating" || key === "underlyingRating" || key === "minimumRatingMoody") {
      result.push({ key, label: formatFieldLabel(key), value: formatBooleanValue(key, value) });
      return;
    }

    if (key.startsWith("min") || key.startsWith("max")) {
      const baseKey = key.replace(/^(min|max)/, "");
      const hasMinVersion = entries.some(([k]) => k === `min${baseKey}`);
      const hasMaxVersion = entries.some(([k]) => k === `max${baseKey}`);

      if (hasMinVersion || hasMaxVersion) {
        if (processedRangeKeys.has(baseKey)) return;
        processedRangeKeys.add(baseKey);
        const minValue = input[`min${baseKey}`];
        const maxValue = input[`max${baseKey}`];
        if (minValue !== undefined || maxValue !== undefined) {
          const dedupeKey = minValue !== undefined ? `min${baseKey}` : `max${baseKey}`;
          result.push({
            key: dedupeKey,
            label: formatFieldLabel(baseKey),
            value: formatRange(minValue, maxValue, baseKey),
          });
        }
      } else {
        result.push({ key, label: formatFieldLabel(key), value: formatBooleanValue(key, value) });
      }
    } else {
      result.push({ key, label: formatFieldLabel(key), value: formatBooleanValue(key, value) });
    }
  });

  return result.filter(
    (e) => e.value !== undefined && e.value !== null && e.value !== ""
  );
};
